import { useAuth } from "@via/hooks/useAuth";
import {
  useRegister,
  useVerifyEmail,
} from "@via/pages/authentication/self-service-signup/authentication.api.tsx";
import EmailConfirmation from "@via/pages/authentication/self-service-signup/email-confirmation.tsx";
import { EmailRegisterForm } from "@via/pages/authentication/self-service-signup/email-register-form.tsx";
import SelfSignupProgress from "@via/pages/authentication/self-service-signup/progress/self-signup-progress.tsx";
import { ROUTES } from "@via/utils/constants.tsx";
import { signUpOptions } from "@via/utils/form-validators.tsx";
import {
  consumePostLoginRedirect,
  isBackendRoute,
} from "@via/utils/post-login-redirect";
import { OnboardingStep, RegisterRequestBody } from "common";
import { type JSX, useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { useGetInvitationCheck } from "@via/pages/organization/organization.api.tsx";
import { useNotifications } from "@via/hooks/useNotifications";
import { NOTIFICATION } from "@via/context/notifications-context";
import { ErrorNotification } from "@via/utils/interfaces";

enum SignupComponentView {
  RegisterForm = 1,
  EmailConfirmation = 2,
  SignupProgress = 3,
}

export default function SelfSignUp(): JSX.Element {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");
  const userEmail = searchParams.get("email");

  const {
    data: invitationDetails,
    isSuccess: isInvitationDetailsSuccess,
    isError: isInvitationDetailsError,
    error: invitationDetailsError,
  } = useGetInvitationCheck(token);

  const { setNotification } = useNotifications();
  const { pathname } = useLocation();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [componentView, setComponentView] = useState<SignupComponentView>(
    SignupComponentView.RegisterForm
  );
  const {
    watch,
    register,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm<RegisterRequestBody>({
    mode: "onChange",
    defaultValues: {
      email: userEmail || "",
    },
  });

  useEffect(() => {
    if (invitationDetails?.invitedEmail) {
      setValue("email", invitationDetails?.invitedEmail);
    }
  }, [isInvitationDetailsSuccess, invitationDetails]);

  useEffect(() => {
    if (isInvitationDetailsError && invitationDetailsError) {
      setNotification({
        id: "invitation-check-error",
        message: "Invitation failed",
        details:
          (invitationDetailsError as ErrorNotification)?.response?.data
            ?.message || "An error occurred while checking the invitation.",
        variant: NOTIFICATION.ERROR,
      });
    }
  }, [isInvitationDetailsError, invitationDetailsError]);

  const {
    mutateAsync: registerEmail,
    isPending: isRegisterEmailLoading,
    isSuccess: isRegisterEmailSuccess,
  } = useRegister();

  const {
    mutate: verifyEmail,
    isPending: isVerifyEmailLoading,
    isSuccess: isVerifyEmailSuccess,
    isError: isVerifyEmailError,
  } = useVerifyEmail();

  useEffect(() => {
    if (isRegisterEmailSuccess) {
      setComponentView(SignupComponentView.EmailConfirmation);
    }
  }, [isRegisterEmailSuccess]);

  useEffect(() => {
    if (isVerifyEmailSuccess) {
      setComponentView(SignupComponentView.SignupProgress);
    }
  }, [isVerifyEmailSuccess]);

  const emailValue = watch("email");

  useEffect(() => {
    if (user?.session && pathname === ROUTES.SELF_SIGNUP) {
      // If the user is logged in and has already created an organization, redirect to activity page
      const shouldBlockAccess =
        (user?.registrationType !== null &&
          user?.onboardingStep !== OnboardingStep.OrganizationCreated) ||
        (user?.userOrganizations?.[0]?.status === "inactive" &&
          user?.userOrganizations?.[0]?.role?.name === "org_admin");

      if (shouldBlockAccess) {
        setComponentView(SignupComponentView.SignupProgress);
      } else {
        const stored = consumePostLoginRedirect();
        const destination = stored ?? ROUTES.ACTIVITY;
        // A /api/* target (e.g. the OAuth consent resume) is a backend route the
        // SPA router can't reach — full-page-nav instead of navigate().
        if (isBackendRoute(destination)) {
          window.location.assign(destination);
        } else {
          navigate(destination, { replace: true });
        }
      }
    }
  }, [
    user?.session,
    user?.registrationType,
    user?.onboardingStep,
    pathname,
    user?.userOrganizations?.[0]?.status,
    navigate,
  ]);

  const handleEmailRegister = async (
    form: RegisterRequestBody
  ): Promise<void> => {
    if (invitationDetails?.invitedEmail) {
      setComponentView(SignupComponentView.SignupProgress);
    } else {
      await registerEmail(form);
    }
  };

  const handleResendConfirmation = async () => {
    await registerEmail({ email: emailValue, isResend: true });
  };

  const handleVerifyConfirmation = (value: string) => {
    verifyEmail({ email: emailValue, verificationCode: value });
  };

  return (
    <>
      <div className="flex flex-1 flex-col py-6 sm:px-8 lg:px-8">
        <div className="mx-8 lg:mx-24 flex h-full flex-row items-center justify-center">
          <div className="flex flex-col items-center justify-center">
            {componentView === SignupComponentView.RegisterForm ? (
              <EmailRegisterForm
                register={register}
                errors={errors}
                isRegisterEmailLoading={isRegisterEmailLoading}
                onSubmit={handleSubmit(handleEmailRegister)}
                signUpOptions={signUpOptions}
                invitationDetails={invitationDetails}
              />
            ) : componentView === SignupComponentView.EmailConfirmation ? (
              <>
                <EmailConfirmation
                  email={emailValue}
                  onVerify={handleVerifyConfirmation}
                  onResend={handleResendConfirmation}
                  isError={isVerifyEmailError}
                  isLoading={isVerifyEmailLoading}
                />
              </>
            ) : componentView === SignupComponentView.SignupProgress ? (
              <SelfSignupProgress email={user?.email || emailValue} />
            ) : null}
          </div>
        </div>
      </div>
    </>
  );
}
